import type { Dispatch, SetStateAction } from 'react';
import { formatContactEmailsInput } from './buildClaimGenerationPayload';
import { applyParsedParserAddressFromRaw, firstScalar, parseParserStructuredAddress } from './claimIntakeParser';
import {
  makeEmptyLaborLineRow,
  makeEmptyPartLineRow,
  nextClaimIntakeLineId,
  normalizeRepairOrderDateToMdy,
  sanitizeVinInput,
  type ClaimLaborLineRow,
  type ClaimPartLineRow,
  type ClaimRepairEntry,
} from './claimIntakeUtils';
import type { VehicleNotesFormState } from './vehicleNotesUtils';

export type ClaimIntakeVoiceFormSetters = Readonly<{
  setLookupVin: (v: string) => void;
  setLookupContractNumber: (v: string) => void;
  setArrivalMethod: (v: string) => void;
  setAtServiceCenter: (v: string) => void;
  setStep3Reimbursement: (v: string) => void;
  setStep3PaymentEmail: (v: string) => void;
  setStep3ContractNumber: (v: string) => void;
  setStep3MileageIn: (v: string) => void;
  setStep3InvoiceNumber: (v: string) => void;
  setStep3RepairOrderDate: (v: string) => void;
  setStep3VehicleVin: (v: string) => void;
  setStep3CustomerName: (v: string) => void;
  setStep3CustomerPhone: (v: string) => void;
  setStep3CustomerAddress1: (v: string) => void;
  setStep3CustomerAddress2: (v: string) => void;
  setStep3CustomerCity: (v: string) => void;
  setStep3CustomerState: (v: string) => void;
  setStep3CustomerPostalCode: (v: string) => void;
  setStep3CustomerCountryCode: (v: string) => void;
  setStep3ServicerName: (v: string) => void;
  setStep3ServicerPhone: (v: string) => void;
  setStep3ContactEmail: (v: string) => void;
  setStep3ServicerContactName: (v: string) => void;
  setStep3ServicerMobile: (v: string) => void;
  setStep3WantText: (v: string) => void;
  setStep3LaborRate: (v: string) => void;
  setStep3ServicerAddress1: (v: string) => void;
  setStep3ServicerAddress2: (v: string) => void;
  setStep3ServicerCity: (v: string) => void;
  setStep3ServicerState: (v: string) => void;
  setStep3ServicerPostalCode: (v: string) => void;
  setStep3ServicerCountryCode: (v: string) => void;
  setStep3DealerNumber: (v: string) => void;
  setStep3Repairs: Dispatch<SetStateAction<ClaimRepairEntry[]>>;
  setVehicleNotes: Dispatch<SetStateAction<VehicleNotesFormState>>;
}>;

const VEHICLE_NOTES_PATH_TO_FORM: ReadonlyArray<
  readonly [payloadKey: string, formKey: keyof VehicleNotesFormState]
> = [
  ['parts_labor_warranty_months', 'partsLaborWarrantyMonths'],
  ['parts_labor_warranty_miles', 'partsLaborWarrantyMiles'],
  ['is_vehicle_modified', 'isVehicleModified'],
  ['tire_type', 'tireType'],
  ['tire_section_width_mm', 'tireSectionWidthMm'],
  ['tire_aspect_ratio', 'tireAspectRatio'],
  ['tire_wheel_diameter_in', 'tireWheelDiameterIn'],
  ['oem_tire_section_width_mm', 'oemTireSectionWidthMm'],
  ['oem_tire_aspect_ratio', 'oemTireAspectRatio'],
  ['oem_tire_wheel_diameter_in', 'oemTireWheelDiameterIn'],
  ['has_lift_kit', 'hasLiftKit'],
  ['lift_amount_inches', 'liftAmountInches'],
  ['has_engine_modifications', 'hasEngineModifications'],
  ['engine_modification_description', 'engineModificationDescription'],
  ['coolant_level', 'coolantLevel'],
  ['coolant_condition', 'coolantCondition'],
  ['engine_oil_level', 'engineOilLevel'],
  ['engine_oil_condition', 'engineOilCondition'],
  ['transmission_fluid_level', 'transmissionFluidLevel'],
  ['transmission_fluid_condition', 'transmissionFluidCondition'],
  ['has_leaks', 'hasLeaks'],
  ['leak_location', 'leakLocation'],
  ['leak_drip_condition', 'leakDripCondition'],
  ['has_warning_light', 'hasWarningLight'],
  ['codes_and_definitions', 'codesAndDefinitions'],
];

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

/** Voice may wrap fields under `claim`, or return them at the root. */
function unwrapParsedFields(parsed: Record<string, unknown>): Record<string, unknown> {
  const claim = asRecord(parsed.claim);
  if (!claim) return parsed;
  return { ...parsed, ...claim };
}

function getByPath(obj: Record<string, unknown>, path: string): unknown {
  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    const record = asRecord(current);
    if (!record) return undefined;
    current = record[part];
  }
  return current;
}

function pickPath(parsed: Record<string, unknown>, ...paths: string[]): string {
  for (const path of paths) {
    const nested = firstScalar(getByPath(parsed, path));
    if (nested) return nested;
    const flat = firstScalar(parsed[path]);
    if (flat) return flat;
  }
  return '';
}

function pickScalar(parsed: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const direct = firstScalar(parsed[key]);
    if (direct) return direct;
  }
  return '';
}

/** VIN + contract number from voice `parsed_fields` (nested claim shape or legacy). */
export function extractLookupIdentityFromParsed(parsedRaw: Record<string, unknown>): {
  vin: string;
  contractNumber: string;
} {
  const parsed = unwrapParsedFields(parsedRaw);
  const contractNumber =
    pickPath(parsed, 'policies.policy_id') ||
    pickScalar(parsed, 'contract_number', 'policies.policy_id');
  const vinRaw =
    pickPath(parsed, 'product.product_id') ||
    pickScalar(
      parsed,
      'Vehicle_Identification_Number',
      'vehicle_identification_number',
      'contract_lookup.vin',
      'product.product_id'
    );
  return {
    vin: sanitizeVinInput(vinRaw),
    contractNumber: contractNumber.trim(),
  };
}

function readContactEmailInput(...candidates: unknown[]): string {
  for (const candidate of candidates) {
    const formatted = formatContactEmailsInput(candidate).trim();
    if (formatted) return formatted;
  }
  return '';
}

function pickNestedScalar(obj: unknown, ...fieldNames: string[]): string {
  const o = asRecord(obj);
  if (!o) return '';
  for (const name of fieldNames) {
    const v = firstScalar(o[name]);
    if (v) return v;
  }
  return '';
}

function normalizeArrivalMethod(raw: string): string {
  const s = raw.trim().toLowerCase();
  if (s === 'driven' || s === 'driven in' || s === 'driven_in') return 'driven';
  if (s === 'towed') return 'towed';
  return raw.trim();
}

function normalizeYesNo(raw: string): string {
  const s = raw.trim().toLowerCase();
  if (s === 'yes' || s === 'y') return 'yes';
  if (s === 'no' || s === 'n') return 'no';
  return raw.trim();
}

function normalizeReimbursement(raw: string): string {
  const s = raw.trim();
  const lower = s.toLowerCase();
  if (lower === 'check') return 'Check';
  if (s === 'CONNEXPAY' || lower === 'credit card' || lower === 'creditcard') return 'CONNEXPAY';
  return s;
}

export { normalizeRepairOrderDateToMdy as normalizeRepairOrderDate } from './claimIntakeUtils';

function normalizeLaborHours(raw: string): string {
  const t = raw.trim();
  if (!t) return '';
  const num = /[\d.]+/.exec(t);
  return num ? num[0] : t;
}

function mapLaborLine(row: unknown): ClaimLaborLineRow {
  const l = asRecord(row) ?? {};
  return {
    id: nextClaimIntakeLineId(),
    labor_description: firstScalar(l.labor_description),
    labor_op_code: firstScalar(l.labor_opcode ?? l.labor_op_code ?? l.labor_repair_notes),
    labor_hours: normalizeLaborHours(firstScalar(l.labor_hours)),
    labor_amount: firstScalar(l.labor_amount ?? l.amount),
    allowFixedValue: false,
  };
}

function mapPartLine(row: unknown): ClaimPartLineRow {
  const p = asRecord(row) ?? {};
  return {
    id: nextClaimIntakeLineId(),
    part_number: firstScalar(p.part_number),
    part_description: firstScalar(p.part_description),
    part_quantity: firstScalar(p.part_quantity),
    part_unit_price: firstScalar(p.unit_cost ?? p.part_unit_price),
    part_amount: firstScalar(p.amount ?? p.part_amount),
  };
}

function pickLineArray(row: Record<string, unknown>, primary: string, fallback: string): unknown[] {
  const primaryVal = row[primary];
  if (Array.isArray(primaryVal)) return primaryVal;
  const fallbackVal = row[fallback];
  if (Array.isArray(fallbackVal)) return fallbackVal;
  return [];
}

function mapRepairRow(row: unknown, id: number): ClaimRepairEntry {
  const r = asRecord(row) ?? {};
  const laborRaw = pickLineArray(r, 'labor', 'labor_lines');
  const partRaw = pickLineArray(r, 'parts', 'part_lines');

  const labor_lines =
    laborRaw.length > 0
      ? laborRaw.map((line) => mapLaborLine(line))
      : [makeEmptyLaborLineRow()];
  const part_lines =
    partRaw.length > 0 ? partRaw.map((line) => mapPartLine(line)) : [makeEmptyPartLineRow()];

  return {
    id,
    complaint: firstScalar(r.complaint_description ?? r.complaint),
    cause: firstScalar(r.cause_description ?? r.cause),
    correction: firstScalar(r.corrective_action_description ?? r.correction),
    tiretread: firstScalar(r.tiretread),
    labor_lines,
    part_lines,
  };
}

type RepairDraft = {
  complaint?: string;
  cause?: string;
  correction?: string;
  tiretread?: string;
  labor: Map<number, Partial<ClaimLaborLineRow>>;
  parts: Map<number, Partial<ClaimPartLineRow>>;
};

function ensureRepairDraft(repairMap: Map<number, RepairDraft>, idx: number): RepairDraft {
  const existing = repairMap.get(idx);
  if (existing) return existing;
  const created: RepairDraft = { labor: new Map(), parts: new Map() };
  repairMap.set(idx, created);
  return created;
}

function applyThreeCDotField(draft: RepairDraft, field: string, value: string): void {
  if (field === 'complaint_description' || field === 'complaint') {
    draft.complaint = value;
    return;
  }
  if (field === 'cause_description' || field === 'cause') {
    draft.cause = value;
    return;
  }
  if (field === 'corrective_action_description' || field === 'correction') {
    draft.correction = value;
    return;
  }
  if (field === 'tiretread') draft.tiretread = value;
}

function applyLaborDotField(line: Partial<ClaimLaborLineRow>, field: string, value: string): void {
  if (field === 'labor_hours') {
    line.labor_hours = normalizeLaborHours(value);
    return;
  }
  if (field === 'labor_opcode') {
    line.labor_op_code = value;
    return;
  }
  if (field === 'amount') {
    line.labor_amount = value;
    return;
  }
  (line as Record<string, string>)[field] = value;
}

function applyPartDotField(line: Partial<ClaimPartLineRow>, field: string, value: string): void {
  if (field === 'unit_cost') {
    line.part_unit_price = value;
    return;
  }
  if (field === 'amount') {
    line.part_amount = value;
    return;
  }
  (line as Record<string, string>)[field] = value;
}

function ingestRepairDotKey(
  repairMap: Map<number, RepairDraft>,
  key: string,
  value: unknown
): void {
  const v = firstScalar(value);
  if (!v) return;

  const threeC =
    /^requests\.(\d+)\.(complaint_description|cause_description|corrective_action_description|tiretread)$/.exec(
      key
    ) ?? /^repairs\.(\d+)\.(complaint|cause|correction|tiretread)$/.exec(key);
  if (threeC) {
    applyThreeCDotField(ensureRepairDraft(repairMap, Number(threeC[1])), threeC[2], v);
    return;
  }

  const labor =
    /^requests\.(\d+)\.labor\.(\d+)\.(\w+)$/.exec(key) ??
    /^repairs\.(\d+)\.labor_lines\.(\d+)\.(\w+)$/.exec(key);
  if (labor) {
    const draft = ensureRepairDraft(repairMap, Number(labor[1]));
    const lineIdx = Number(labor[2]);
    const line = draft.labor.get(lineIdx) ?? {};
    applyLaborDotField(line, labor[3], v);
    draft.labor.set(lineIdx, line);
    return;
  }

  const part =
    /^requests\.(\d+)\.parts\.(\d+)\.(\w+)$/.exec(key) ??
    /^repairs\.(\d+)\.part_lines\.(\d+)\.(\w+)$/.exec(key);
  if (!part) return;

  const draft = ensureRepairDraft(repairMap, Number(part[1]));
  const lineIdx = Number(part[2]);
  const line = draft.parts.get(lineIdx) ?? {};
  applyPartDotField(line, part[3], v);
  draft.parts.set(lineIdx, line);
}

function finalizeRepairDraft(
  entry: RepairDraft,
  displayId: number
): ClaimRepairEntry {
  const laborIdxs = [...entry.labor.keys()].sort((a, b) => a - b);
  const partIdxs = [...entry.parts.keys()].sort((a, b) => a - b);

  const labor_lines: ClaimLaborLineRow[] =
    laborIdxs.length > 0
      ? laborIdxs.map((li) => {
          const partial = entry.labor.get(li)!;
          return {
            id: nextClaimIntakeLineId(),
            labor_description: partial.labor_description ?? '',
            labor_op_code: partial.labor_op_code ?? '',
            labor_hours: partial.labor_hours ?? '',
            labor_amount: partial.labor_amount ?? '',
            allowFixedValue: false,
          };
        })
      : [makeEmptyLaborLineRow()];

  const part_lines: ClaimPartLineRow[] =
    partIdxs.length > 0
      ? partIdxs.map((pi) => {
          const partial = entry.parts.get(pi)!;
          return {
            id: nextClaimIntakeLineId(),
            part_number: partial.part_number ?? '',
            part_description: partial.part_description ?? '',
            part_quantity: partial.part_quantity ?? '',
            part_unit_price: partial.part_unit_price ?? '',
            part_amount: partial.part_amount ?? '',
          };
        })
      : [makeEmptyPartLineRow()];

  return {
    id: displayId + 1,
    complaint: entry.complaint ?? '',
    cause: entry.cause ?? '',
    correction: entry.correction ?? '',
    tiretread: entry.tiretread ?? '',
    labor_lines,
    part_lines,
  };
}

function buildRepairsFromDotKeys(parsed: Record<string, unknown>): ClaimRepairEntry[] {
  const repairMap = new Map<number, RepairDraft>();
  for (const [key, value] of Object.entries(parsed)) {
    ingestRepairDotKey(repairMap, key, value);
  }
  if (repairMap.size === 0) return [];
  const sortedIdx = [...repairMap.keys()].sort((a, b) => a - b);
  return sortedIdx.map((repairIdx, displayId) =>
    finalizeRepairDraft(repairMap.get(repairIdx)!, displayId)
  );
}

function buildRepairsFromParsed(parsed: Record<string, unknown>): ClaimRepairEntry[] | null {
  const requestsRaw = parsed.requests;
  if (Array.isArray(requestsRaw) && requestsRaw.length > 0) {
    return requestsRaw.map((row, i) => mapRepairRow(row, i + 1));
  }
  const repairsRaw = parsed.repairs;
  if (Array.isArray(repairsRaw) && repairsRaw.length > 0) {
    return repairsRaw.map((row, i) => mapRepairRow(row, i + 1));
  }
  const fromDots = buildRepairsFromDotKeys(parsed);
  return fromDots.length > 0 ? fromDots : null;
}

function setIfNonEmpty(setter: (v: string) => void, value: string): boolean {
  const v = value.trim();
  if (!v) return false;
  setter(v);
  return true;
}

function applyAddressFromObject(
  address: unknown,
  setters: {
    onAddress1Change: (v: string) => void;
    onAddress2Change: (v: string) => void;
    onCityChange: (v: string) => void;
    onStateChange: (v: string) => void;
    onPostalCodeChange: (v: string) => void;
    onCountryCodeChange: (v: string) => void;
  }
): boolean {
  const addr = asRecord(address);
  if (!addr) return false;
  let applied = false;
  const apply = (setter: (v: string) => void, value: string) => {
    if (setIfNonEmpty(setter, value)) applied = true;
  };
  apply(setters.onAddress1Change, firstScalar(addr.address1));
  apply(setters.onAddress2Change, firstScalar(addr.address2));
  apply(setters.onCityChange, firstScalar(addr.city));
  apply(setters.onStateChange, firstScalar(addr.state));
  apply(setters.onPostalCodeChange, firstScalar(addr.postal_code ?? addr.postalCode));
  apply(setters.onCountryCodeChange, firstScalar(addr.country_code ?? addr.countryCode));
  return applied;
}

type MutableVehicleNotesPatch = {
  -readonly [K in keyof VehicleNotesFormState]?: VehicleNotesFormState[K];
};

function applyVehicleNotes(
  parsed: Record<string, unknown>,
  setVehicleNotes: Dispatch<SetStateAction<VehicleNotesFormState>>
): boolean {
  const notesObj = asRecord(getByPath(parsed, 'vehicle_notes')) ?? asRecord(parsed.vehicle_notes);
  const patch: MutableVehicleNotesPatch = {};
  const yesNoKeys = new Set<keyof VehicleNotesFormState>([
    'isVehicleModified',
    'hasLiftKit',
    'hasEngineModifications',
    'hasLeaks',
    'hasWarningLight',
  ]);

  for (const [payloadKey, formKey] of VEHICLE_NOTES_PATH_TO_FORM) {
    const fromNested = notesObj ? firstScalar(notesObj[payloadKey]) : '';
    const fromDot = pickPath(parsed, `vehicle_notes.${payloadKey}`);
    const value = (fromNested || fromDot).trim();
    if (!value) continue;
    patch[formKey] = yesNoKeys.has(formKey) ? normalizeYesNo(value) : value;
  }

  if (Object.keys(patch).length === 0) return false;
  setVehicleNotes((prev) => ({ ...prev, ...patch }));
  return true;
}

/**
 * Maps voice-agent `form_capture.parsed_fields` (nested claim shape, dot-keys, or legacy)
 * into claim intake state. Returns true if at least one field was applied.
 */
export function applyVoiceAgentParsedToClaimIntake(
  parsedRaw: Record<string, unknown>,
  setters: ClaimIntakeVoiceFormSetters
): boolean {
  const parsed = unwrapParsedFields(parsedRaw);
  let applied = false;
  const apply = (setter: (v: string) => void, value: string) => {
    if (setIfNonEmpty(setter, value)) applied = true;
  };

  const contractNumber =
    pickPath(parsed, 'policies.policy_id') ||
    pickScalar(parsed, 'contract_number', 'policies.policy_id');
  apply(setters.setLookupContractNumber, contractNumber);
  apply(setters.setStep3ContractNumber, contractNumber);

  const vinRaw =
    pickPath(parsed, 'product.product_id') ||
    pickScalar(
      parsed,
      'Vehicle_Identification_Number',
      'vehicle_identification_number',
      'contract_lookup.vin',
      'product.product_id'
    );
  const vin = sanitizeVinInput(vinRaw);
  apply(setters.setLookupVin, vin);
  apply(setters.setStep3VehicleVin, vin);

  apply(
    setters.setArrivalMethod,
    normalizeArrivalMethod(
      pickPath(parsed, 'vehicle_arrival_method') || pickScalar(parsed, 'vehicle_arrival_method')
    )
  );
  apply(
    setters.setAtServiceCenter,
    normalizeYesNo(
      pickPath(parsed, 'vehicle_at_service_center') ||
        pickScalar(parsed, 'vehicle_at_service_center')
    )
  );
  apply(
    setters.setStep3Reimbursement,
    normalizeReimbursement(
      pickPath(parsed, 'reimbursement_method') || pickScalar(parsed, 'reimbursement_method')
    )
  );
  apply(
    setters.setStep3PaymentEmail,
    pickPath(parsed, 'payment_email') || pickScalar(parsed, 'payment_email')
  );

  apply(
    setters.setStep3InvoiceNumber,
    pickPath(parsed, 'invoice_number') || pickScalar(parsed, 'Invoice_Number', 'invoice_number')
  );
  apply(
    setters.setStep3MileageIn,
    pickPath(parsed, 'failure_odometer') || pickScalar(parsed, 'MILEGE_IN', 'milege_in', 'failure_odometer')
  );
  apply(
    setters.setStep3RepairOrderDate,
    normalizeRepairOrderDateToMdy(
      pickPath(parsed, 'repair_order_date') || pickScalar(parsed, 'repair_order_date')
    )
  );
  apply(
    setters.setStep3LaborRate,
    pickPath(parsed, 'labor_rate') ||
      pickNestedScalar(parsed.service_center_information, 'labor_rate') ||
      pickScalar(parsed, 'service_center_information.labor_rate', 'labor_rate')
  );

  const customer = asRecord(getByPath(parsed, 'customer')) ?? asRecord(parsed.customer);
  apply(
    setters.setStep3CustomerName,
    pickNestedScalar(customer, 'name') ||
      pickPath(parsed, 'customer.name') ||
      pickScalar(parsed, 'customer_name')
  );
  apply(
    setters.setStep3CustomerPhone,
    pickNestedScalar(customer, 'phone_no') ||
      pickPath(parsed, 'customer.phone_no') ||
      pickScalar(parsed, 'customer_phone_number')
  );

  const customerAddressApplied = applyAddressFromObject(customer?.address, {
    onAddress1Change: setters.setStep3CustomerAddress1,
    onAddress2Change: setters.setStep3CustomerAddress2,
    onCityChange: setters.setStep3CustomerCity,
    onStateChange: setters.setStep3CustomerState,
    onPostalCodeChange: setters.setStep3CustomerPostalCode,
    onCountryCodeChange: setters.setStep3CustomerCountryCode,
  });
  if (customerAddressApplied) applied = true;

  if (!customerAddressApplied) {
    apply(
      setters.setStep3CustomerAddress1,
      pickPath(parsed, 'customer.address.address1')
    );
    apply(
      setters.setStep3CustomerAddress2,
      pickPath(parsed, 'customer.address.address2')
    );
    apply(setters.setStep3CustomerCity, pickPath(parsed, 'customer.address.city'));
    apply(setters.setStep3CustomerState, pickPath(parsed, 'customer.address.state'));
    apply(
      setters.setStep3CustomerPostalCode,
      pickPath(parsed, 'customer.address.postal_code')
    );
    apply(
      setters.setStep3CustomerCountryCode,
      pickPath(parsed, 'customer.address.country_code')
    );
    applyParsedParserAddressFromRaw(parsed.customer_address, {
      onAddress1Change: setters.setStep3CustomerAddress1,
      onAddress2Change: setters.setStep3CustomerAddress2,
      onCityChange: setters.setStep3CustomerCity,
      onStateChange: setters.setStep3CustomerState,
      onPostalCodeChange: setters.setStep3CustomerPostalCode,
      onCountryCodeChange: setters.setStep3CustomerCountryCode,
    });
  }

  const servicer = asRecord(getByPath(parsed, 'servicer')) ?? asRecord(parsed.servicer);
  const sci = parsed.service_center_information;
  apply(
    setters.setStep3ServicerName,
    pickNestedScalar(servicer, 'name') ||
      pickPath(parsed, 'servicer.name') ||
      pickNestedScalar(sci, 'servicer_name') ||
      pickScalar(parsed, 'service_center_information.servicer_name', 'invoice_generator_name')
  );
  apply(
    setters.setStep3ServicerPhone,
    pickNestedScalar(servicer, 'phone_no') ||
      pickPath(parsed, 'servicer.phone_no') ||
      pickNestedScalar(sci, 'servicer_phone') ||
      pickScalar(parsed, 'service_center_information.servicer_phone')
  );
  apply(
    setters.setStep3ContactEmail,
    readContactEmailInput(
      servicer?.email,
      getByPath(parsed, 'servicer.email'),
      asRecord(sci)?.contact_email,
      getByPath(parsed, 'service_center_information.contact_email'),
      parsed['service_center_information.contact_email']
    )
  );
  apply(
    setters.setStep3ServicerContactName,
    pickNestedScalar(servicer, 'contact_name') || pickPath(parsed, 'servicer.contact_name')
  );
  apply(
    setters.setStep3ServicerMobile,
    pickNestedScalar(servicer, 'mobile_no') || pickPath(parsed, 'servicer.mobile_no')
  );
  apply(
    setters.setStep3WantText,
    normalizeYesNo(
      pickNestedScalar(servicer, 'want_text') || pickPath(parsed, 'servicer.want_text')
    )
  );
  apply(
    setters.setStep3DealerNumber,
    pickNestedScalar(servicer, 'dealer_no') || pickPath(parsed, 'servicer.dealer_no')
  );

  const servicerAddressApplied = applyAddressFromObject(servicer?.address, {
    onAddress1Change: setters.setStep3ServicerAddress1,
    onAddress2Change: setters.setStep3ServicerAddress2,
    onCityChange: setters.setStep3ServicerCity,
    onStateChange: setters.setStep3ServicerState,
    onPostalCodeChange: setters.setStep3ServicerPostalCode,
    onCountryCodeChange: setters.setStep3ServicerCountryCode,
  });
  if (servicerAddressApplied) applied = true;

  if (!servicerAddressApplied) {
    apply(setters.setStep3ServicerAddress1, pickPath(parsed, 'servicer.address.address1'));
    apply(setters.setStep3ServicerAddress2, pickPath(parsed, 'servicer.address.address2'));
    apply(setters.setStep3ServicerCity, pickPath(parsed, 'servicer.address.city'));
    apply(setters.setStep3ServicerState, pickPath(parsed, 'servicer.address.state'));
    apply(
      setters.setStep3ServicerPostalCode,
      pickPath(parsed, 'servicer.address.postal_code')
    );
    apply(
      setters.setStep3ServicerCountryCode,
      pickPath(parsed, 'servicer.address.country_code')
    );

    const phys =
      asRecord(sci)?.physical_address ??
      getByPath(parsed, 'service_center_information.physical_address');
    const invoiceAddr = parseParserStructuredAddress(parsed.invoice_generator_address);
    const physAddr = parseParserStructuredAddress(phys);
    apply(setters.setStep3ServicerAddress1, invoiceAddr.address1 || physAddr.address1);
    apply(setters.setStep3ServicerAddress2, invoiceAddr.address2 || physAddr.address2);
    apply(setters.setStep3ServicerCity, invoiceAddr.city || physAddr.city);
    apply(setters.setStep3ServicerState, invoiceAddr.state || physAddr.state);
    apply(
      setters.setStep3ServicerPostalCode,
      invoiceAddr.postalCode ||
        physAddr.postalCode ||
        pickPath(parsed, 'service_center_information.physical_address.postalCode')
    );
    apply(setters.setStep3ServicerCountryCode, invoiceAddr.countryCode || physAddr.countryCode);
  }

  if (applyVehicleNotes(parsed, setters.setVehicleNotes)) applied = true;

  const repairs = buildRepairsFromParsed(parsed);
  if (repairs && repairs.length > 0) {
    setters.setStep3Repairs(repairs);
    applied = true;
  }

  return applied;
}
